export type website_media_locale = "fr" | "en";

export type website_media_page =
  | "global"
  | "home"
  | "about"
  | "resources"
  | "pricing"
  | "services";

export type website_media_section =
  | "avatars"
  | "photos"
  | "fallbacks"
  | "hero"
  | "social_share";

export type WebsiteMediaPlacementKey =
  | "home_freelancer_avatar_1"
  | "home_freelancer_avatar_2"
  | "home_freelancer_avatar_3"
  | "home_freelancer_avatar_4"
  | "home_freelancer_avatar_5"
  | "home_freelancer_avatar_6"
  | "about_photo_1"
  | "about_photo_2"
  | "about_photo_3"
  | "home_dashboard_photo"
  | "home_guide_pigistes_photo"
  | "home_guide_recruteurs_photo"
  | "home_feature_pigistes_photo"
  | "home_feature_annonces_photo"
  | "home_feature_alliances_photo"
  | "resources_featured_fallback"
  | "resources_freelancer_fallback"
  | "resources_recruiter_fallback"
  | "resources_other_fallback"
  | "pricing_hero_photo"
  | "services_hero_photo"
  | "social_share_image";

/** Order pages appear in the admin UI. A placement whose `page` isn't listed here renders nowhere in the admin. */
export const WEBSITE_MEDIA_PAGE_ORDER: readonly website_media_page[] = [
  "global",
  "home",
  "about",
  "services",
  "resources",
  "pricing",
];

export interface WebsiteMediaEntry {
  id: string;
  placement_key: WebsiteMediaPlacementKey;
  image_url: string | null;
  alt_text_fr: string | null;
  alt_text_en: string | null;
  created_at: string;
  updated_at: string;
}

export interface website_media_definition {
  key: WebsiteMediaPlacementKey;
  page: website_media_page;
  section: website_media_section;
  group_label: string;
  label: string;
  sort_order: number;
  default_image_url: string;
  default_alt_text_fr: string;
  default_alt_text_en: string;
}

export interface WebsiteTestimonial {
  id: string;
  sort_order: number;
  image_url: string | null;
  alt_text_fr: string | null;
  alt_text_en: string | null;
  person_name: string;
  person_title_fr: string;
  person_title_en: string;
  quote_fr: string | null;
  quote_en: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebsiteSpotlightCard {
  id: string;
  sort_order: number;
  image_url: string | null;
  alt_text_fr: string | null;
  alt_text_en: string | null;
  title_fr: string;
  title_en: string;
  description_fr: string;
  description_en: string;
  cta_text_fr: string;
  cta_text_en: string;
  cta_link: string;
  open_in_new_tab: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebsiteCommunityGalleryItem {
  id: string;
  sort_order: number;
  image_url: string | null;
  alt_text_fr: string | null;
  alt_text_en: string | null;
  created_at: string;
  updated_at: string;
}

export type WebsitePracticalHighlightType = "text" | "image";

export interface WebsitePracticalHighlight {
  id: string;
  sort_order: number;
  item_type: WebsitePracticalHighlightType;
  image_url: string | null;
  alt_text_fr: string | null;
  alt_text_en: string | null;
  text_fr: string | null;
  text_en: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebsiteCampaign {
  id: string;
  slug: string;
  published: boolean;
  profession: string;
  badge_fr: string | null;
  badge_en: string | null;
  headline_fr: string;
  headline_en: string;
  subheadline_fr: string | null;
  subheadline_en: string | null;
  hero_image_url: string | null;
  hero_image_alt_fr: string | null;
  hero_image_alt_en: string | null;
  benefit_1_fr: string | null;
  benefit_1_en: string | null;
  benefit_2_fr: string | null;
  benefit_2_en: string | null;
  benefit_3_fr: string | null;
  benefit_3_en: string | null;
  benefit_4_fr: string | null;
  benefit_4_en: string | null;
  gallery_image_urls: string[];
  cta_text_fr: string | null;
  cta_text_en: string | null;
  created_at: string;
  updated_at: string;
}

export type website_campaign_seed = Omit<WebsiteCampaign, 'id' | 'created_at' | 'updated_at'>;

export type website_testimonial_seed = Omit<
  WebsiteTestimonial,
  "id" | "created_at" | "updated_at"
>;

export type website_spotlight_card_seed = Omit<
  WebsiteSpotlightCard,
  "id" | "created_at" | "updated_at"
>;

export type website_community_gallery_item_seed = Omit<
  WebsiteCommunityGalleryItem,
  "id" | "created_at" | "updated_at"
>;

export type website_practical_highlight_seed = Omit<
  WebsitePracticalHighlight,
  "id" | "created_at" | "updated_at"
>;

export const WEBSITE_MEDIA_PLACEMENTS: website_media_definition[] = [
  {
    key: "about_photo_1",
    page: "about",
    section: "photos",
    group_label: "Photos",
    label: "1st photo",
    sort_order: 20,
    default_image_url: "/assets/images/pg-proto/apropos-fondateurs.webp",
    default_alt_text_fr: "Les fondateurs de Pigeons",
    default_alt_text_en: "The Pigeons founders",
  },
  {
    key: "about_photo_2",
    page: "about",
    section: "photos",
    group_label: "Photos",
    label: "2nd photo",
    sort_order: 30,
    default_image_url: "/assets/images/pg-proto/apropos-alliance.webp",
    default_alt_text_fr: "Une alliance de pigistes en collaboration",
    default_alt_text_en: "A freelancer alliance collaborating",
  },
  {
    key: "home_freelancer_avatar_1",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 1",
    sort_order: 11,
    default_image_url: "/assets/images/pg-proto/avatar-1.webp",
    default_alt_text_fr: "Photo de pigiste 1",
    default_alt_text_en: "Freelancer photo 1",
  },
  {
    key: "home_freelancer_avatar_2",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 2",
    sort_order: 12,
    default_image_url: "/assets/images/pg-proto/avatar-2.webp",
    default_alt_text_fr: "Photo de pigiste 2",
    default_alt_text_en: "Freelancer photo 2",
  },
  {
    key: "home_freelancer_avatar_3",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 3",
    sort_order: 13,
    default_image_url: "/assets/images/pg-proto/avatar-3.webp",
    default_alt_text_fr: "Photo de pigiste 3",
    default_alt_text_en: "Freelancer photo 3",
  },
  {
    key: "home_freelancer_avatar_4",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 4",
    sort_order: 14,
    default_image_url: "/assets/images/pg-proto/avatar-4.webp",
    default_alt_text_fr: "Photo de pigiste 4",
    default_alt_text_en: "Freelancer photo 4",
  },
  {
    key: "home_freelancer_avatar_5",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 5",
    sort_order: 15,
    default_image_url: "/assets/images/pg-proto/avatar-5.webp",
    default_alt_text_fr: "Photo de pigiste 5",
    default_alt_text_en: "Freelancer photo 5",
  },
  {
    key: "home_freelancer_avatar_6",
    page: "home",
    section: "avatars",
    group_label: "Photos des pigistes",
    label: "Photo 6",
    sort_order: 16,
    default_image_url: "/assets/images/pg-proto/avatar-6.webp",
    default_alt_text_fr: "Photo de pigiste 6",
    default_alt_text_en: "Freelancer photo 6",
  },
  {
    key: "about_photo_3",
    page: "about",
    section: "photos",
    group_label: "Photos",
    label: "3rd photo",
    sort_order: 35,
    default_image_url: "/assets/images/pg-proto/apropos-collab.webp",
    default_alt_text_fr: "Collaboration entre pigistes et recruteurs",
    default_alt_text_en: "Freelancers and recruiters collaborating",
  },
  {
    key: "home_dashboard_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Dashboard screenshot",
    sort_order: 17,
    default_image_url: "/assets/images/pg-proto/dashboard-shot.webp",
    default_alt_text_fr: "Capture du tableau de bord",
    default_alt_text_en: "Screenshot of the dashboard",
  },
  {
    key: "home_guide_pigistes_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Freelancer guide photo",
    sort_order: 18,
    default_image_url: "/assets/images/pg-proto/onboarding-pigistes.webp",
    default_alt_text_fr: "Portrait pigiste",
    default_alt_text_en: "Freelancer portrait",
  },
  {
    key: "home_guide_recruteurs_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Recruiter guide photo",
    sort_order: 19,
    default_image_url: "/assets/images/pg-proto/onboarding-recruteurs.webp",
    default_alt_text_fr: "PME de la région",
    default_alt_text_en: "Local SMB",
  },
  {
    key: "home_feature_pigistes_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Feature card: freelancers",
    sort_order: 20,
    default_image_url: "/assets/images/pg-proto/feature-pigistes.webp",
    default_alt_text_fr: "Pigiste dans son atelier",
    default_alt_text_en: "Freelancer in their workshop",
  },
  {
    key: "home_feature_annonces_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Feature card: job posts",
    sort_order: 21,
    default_image_url: "/assets/images/pg-proto/feature-annonces.jpg",
    default_alt_text_fr: "Rencontre avec une PME",
    default_alt_text_en: "Meeting with an SMB",
  },
  {
    key: "home_feature_alliances_photo",
    page: "home",
    section: "photos",
    group_label: "Photos",
    label: "Feature card: alliances",
    sort_order: 22,
    default_image_url: "/assets/images/pg-proto/feature-alliances.webp",
    default_alt_text_fr: "Deux pigistes qui collaborent",
    default_alt_text_en: "Two freelancers collaborating",
  },
  {
    key: "resources_featured_fallback",
    page: "resources",
    section: "fallbacks",
    group_label: "Images de secours",
    label: "Article vedette",
    sort_order: 40,
    default_image_url:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200",
    default_alt_text_fr: "Image de secours pour l'article vedette",
    default_alt_text_en: "Fallback image for the featured article",
  },
  {
    key: "resources_freelancer_fallback",
    page: "resources",
    section: "fallbacks",
    group_label: "Images de secours",
    label: "Section pigistes",
    sort_order: 50,
    default_image_url:
      "https://images.unsplash.com/photo-1542744094-24638eff58bb?q=80&w=800",
    default_alt_text_fr: "Image de secours pour la section pigistes",
    default_alt_text_en: "Fallback image for the freelancer section",
  },
  {
    key: "resources_recruiter_fallback",
    page: "resources",
    section: "fallbacks",
    group_label: "Images de secours",
    label: "Section recruteurs",
    sort_order: 60,
    default_image_url:
      "https://images.unsplash.com/photo-1523240715631-41910609b552?q=80&w=800",
    default_alt_text_fr: "Image de secours pour la section recruteurs",
    default_alt_text_en: "Fallback image for the recruiter section",
  },
  {
    key: "resources_other_fallback",
    page: "resources",
    section: "fallbacks",
    group_label: "Images de secours",
    label: "Autres ressources",
    sort_order: 70,
    default_image_url:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=600",
    default_alt_text_fr: "Image de secours pour les autres ressources",
    default_alt_text_en: "Fallback image for other resources",
  },
  {
    key: "pricing_hero_photo",
    page: "pricing",
    section: "hero",
    group_label: "Bloc hero",
    label: "Photo principale",
    sort_order: 80,
    default_image_url: "/assets/images/pricing_image.jpg",
    default_alt_text_fr:
      "Mème en trois cases : un pigeon triste face aux mots « Abonnement » et « Commission », puis un pigeon souriant face à « Payer seulement quand ça compte ».",
    default_alt_text_en:
      'Three-panel meme: a sad pigeon next to the words "Subscription" and "Commission", then a winking pigeon next to "Pay only when it counts".',
  },
  {
    key: "services_hero_photo",
    page: "services",
    section: "hero",
    group_label: "Bloc hero",
    label: "Photo principale",
    sort_order: 85,
    default_image_url:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1200&auto=format&fit=crop",
    default_alt_text_fr: "Pigiste au travail sur un projet",
    default_alt_text_en: "Freelancer working on a project",
  },
  {
    key: "social_share_image",
    page: "global",
    section: "social_share",
    group_label: "Partage social",
    label: "Image de partage social",
    sort_order: 90,
    default_image_url: "/og/og-default.jpg",
    default_alt_text_fr:
      "Pigeons — la plateforme qui connecte pigistes et recruteurs en région",
    default_alt_text_en:
      "Pigeons — the platform connecting freelancers and recruiters in your region",
  },
];

/** Matches legacy `website/messages` `testimonials` keys (role1–3, quote1–3) and on-site assets under `/public/assets/images/testimonials/`. */
export const DEFAULT_WEBSITE_TESTIMONIALS: website_testimonial_seed[] = [
  {
    sort_order: 10,
    image_url: "/assets/images/testimonials/carol_ann.jpg",
    alt_text_fr: "Portrait de Carol-Ann",
    alt_text_en: "Portrait of Carol-Ann",
    person_name: "Carol-Ann",
    person_title_fr: "Photographe, Rimouski",
    person_title_en: "Photographer, Rimouski",
    quote_fr: "",
    quote_en: "",
  },
  {
    sort_order: 20,
    image_url: "/assets/images/testimonials/carol_anne.jpeg",
    alt_text_fr: "Portrait de Carol-Anne",
    alt_text_en: "Portrait of Carol-Anne",
    person_name: "Carol-Anne",
    person_title_fr: "Gestionnaire Marketing",
    person_title_en: "Marketing Manager",
    quote_fr:
      "C'est vraiment un beau projet ! Pigeons offre la possibilité de trouver des talents que je ne trouverais probablement pas autrement.",
    quote_en:
      "It's really a great project! Pigeons makes it possible to find talent I probably wouldn't find otherwise.",
  },
  {
    sort_order: 30,
    image_url: "/assets/images/testimonials/sam.jpeg",
    alt_text_fr: "Portrait de Samuël",
    alt_text_en: "Portrait of Samuël",
    person_name: "Samuël",
    person_title_fr: "Concepteur-rédacteur, Rimouski",
    person_title_en: "Copywriter, Rimouski",
    quote_fr: "",
    quote_en: "",
  },
];

export const DEFAULT_WEBSITE_SPOTLIGHT_CARDS: website_spotlight_card_seed[] = [
  {
    sort_order: 10,
    image_url:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=800&auto=format&fit=crop",
    alt_text_fr: "Professionnelle souriante",
    alt_text_en: "Smiling professional",
    title_fr: "Les Pigistes",
    title_en: "Freelancers",
    description_fr:
      "Un bottin qui vous fait decouvrir tous les travailleurs.euses autonomes a proximite. (et plus loin encore)",
    description_en:
      "A directory that lets you discover all independent workers nearby (and beyond).",
    cta_text_fr: "Ajoutez votre nom au bottin",
    cta_text_en: "Add your name to the directory",
    cta_link: "/signup",
    open_in_new_tab: false,
  },
  {
    sort_order: 20,
    image_url:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=800&auto=format&fit=crop",
    alt_text_fr: "Equipe en collaboration",
    alt_text_en: "Team collaborating",
    title_fr: "Les Alliances",
    title_en: "Alliances",
    description_fr:
      "Creez des groupes de discussion et collaborez sur des projets locaux en toute simplicite.",
    description_en:
      "Create discussion groups and collaborate on local projects with ease.",
    cta_text_fr: "Trouver des allies",
    cta_text_en: "Find allies",
    cta_link: "/signup",
    open_in_new_tab: false,
  },
  {
    sort_order: 30,
    image_url:
      "https://images.unsplash.com/photo-1434030216411-0bb7c3f3dfad?q=80&w=800&auto=format&fit=crop",
    alt_text_fr: "Personne qui lit au bureau",
    alt_text_en: "Person reading in an office",
    title_fr: "Les Ressources",
    title_en: "Resources",
    description_fr:
      "Accedez a des guides, des modeles de contrats et des formations exclusifs pour votre activite.",
    description_en:
      "Access guides, contract templates and exclusive training for your business.",
    cta_text_fr: "Explorer les ressources",
    cta_text_en: "Explore resources",
    cta_link: "/ressources",
    open_in_new_tab: false,
  },
];

export const DEFAULT_WEBSITE_COMMUNITY_GALLERY_ITEMS: website_community_gallery_item_seed[] =
  [
    {
      sort_order: 10,
      image_url:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Professionnelle souriante",
      alt_text_en: "Smiling professional",
    },
    {
      sort_order: 20,
      image_url:
        "https://images.unsplash.com/photo-1580894894513-541e068a3e2b?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Developpeur au travail",
      alt_text_en: "Developer at work",
    },
    {
      sort_order: 30,
      image_url:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Collaboration creative",
      alt_text_en: "Creative collaboration",
    },
    {
      sort_order: 40,
      image_url:
        "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Equipe en remue-meninges",
      alt_text_en: "Team brainstorming",
    },
  ];

export const DEFAULT_WEBSITE_PRACTICAL_HIGHLIGHTS: website_practical_highlight_seed[] =
  [
    {
      sort_order: 10,
      item_type: "text",
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      text_fr:
        "Les pigistes (particulierement en region) qui veulent se montrer a la bette dans leur ville.",
      text_en:
        "Freelancers (especially in regions) who want to showcase themselves in their city.",
    },
    {
      sort_order: 20,
      item_type: "image",
      image_url:
        "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Personne souriante au telephone",
      alt_text_en: "Smiling person on the phone",
      text_fr: null,
      text_en: null,
    },
    {
      sort_order: 30,
      item_type: "text",
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      text_fr:
        "Les entreprises qui souhaitent sauver des sous en engageant un·e ou plusieurs pigistes, plutot qu'une agence.",
      text_en:
        "Companies looking to save money by hiring one or more freelancers, rather than an agency.",
    },
    {
      sort_order: 40,
      item_type: "image",
      image_url:
        "https://images.unsplash.com/photo-1442512595331-e89e73853f31?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Barista en plein travail",
      alt_text_en: "Barista at work",
      text_fr: null,
      text_en: null,
    },
    {
      sort_order: 50,
      item_type: "text",
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      text_fr:
        "Les collectifs de pigistes qui cherchent a decrocher des mandats plus ambitieux ensemble.",
      text_en:
        "Freelancer collectives looking to land more ambitious projects together.",
    },
    {
      sort_order: 60,
      item_type: "image",
      image_url:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=600&auto=format&fit=crop",
      alt_text_fr: "Scene de collaboration",
      alt_text_en: "Collaboration scene",
      text_fr: null,
      text_en: null,
    },
  ];

export const WEBSITE_MEDIA_PLACEMENT_KEYS = WEBSITE_MEDIA_PLACEMENTS.map(
  ({ key }) => key,
);

export const WEBSITE_MEDIA_DEFINITIONS_BY_KEY = Object.fromEntries(
  WEBSITE_MEDIA_PLACEMENTS.map((definition) => [definition.key, definition]),
) as Record<WebsiteMediaPlacementKey, website_media_definition>;

export const get_website_media_definition = (
  key: WebsiteMediaPlacementKey,
): website_media_definition => WEBSITE_MEDIA_DEFINITIONS_BY_KEY[key];

/**
 * Hostnames allowlisted in `website/next.config.mjs`'s `images.remotePatterns`.
 * Any absolute image URL saved for a website media placement must resolve to
 * one of these hosts (or be a relative path) or `next/image` will throw at
 * render time for every visitor on that page.
 */
export const WEBSITE_MEDIA_ALLOWED_IMAGE_HOSTNAMES: readonly string[] = [
  "images.unsplash.com",
  "cdn.jsdelivr.net",
  "pndhrhokvbsuysyaqhva.supabase.co",
  "obipyoxtxssuownldvmk.supabase.co",
  "baijjsbhuouljdtbtzkk.supabase.co",
  "zrnirnpxzlqeijqykrog.supabase.co",
  "avatars.githubusercontent.com",
  "lh3.googleusercontent.com",
  "ui-avatars.com",
  "api.nouspigeons.ca",
];

/**
 * Returns true when `url` is either a relative path (starts with `/`) or an
 * absolute URL whose hostname is in `WEBSITE_MEDIA_ALLOWED_IMAGE_HOSTNAMES`.
 */
export const is_allowed_website_media_image_url = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith("/")) {
    return true;
  }

  try {
    const parsed = new URL(trimmed);
    return WEBSITE_MEDIA_ALLOWED_IMAGE_HOSTNAMES.includes(parsed.hostname);
  } catch {
    return false;
  }
};
